import { getTextStyle } from '../scripts/FontConfig.js';

export default class OverlayUI {
  constructor(scene, options = {}) {
    this.scene = scene;
    this.overlayContainer = null;
    this.friendSlots = [];
    this.menuButton = null;
    this.navigationButtons = null;
    
    // Options for customizing the overlay
    this.showNavigation = options.showNavigation || false;
    
    // Track continuous movement
    this.movementTimer = null;
    this.currentMovement = { x: 0, y: 0 };
    
    // Track which friends have been rescued
    this.rescuedFriends = this.loadRescuedFriends();
    
    // Store tilemap and placeholders
    this.friendsUIMap = null;
    this.placeholders = {};
  }

  create() {
    // Load the friends UI tilemap
    this.loadFriendsUIMap();
    
    // Create the main overlay container
    this.createOverlayBackground();
    this.setupFriendPlaceholders();
    this.createMenuButton();
    
    // Create navigation buttons if enabled
    if (this.showNavigation) {
      this.createNavigationButtons();
    }
    
    // Update the display based on saved progress
    this.updateFriendDisplay();
  }

  loadFriendsUIMap() {
    try {
      // Create the friends UI tilemap
      try {
        this.friendsUIMap = this.scene.make.tilemap({ key: 'friends-ui' });
        this.friendsUIMap.addTilesetImage("ui");
      } catch (error) {
        console.warn('Could not load friends-ui tilemap:', error.message);
      }
      

      // Create tile layers (L1, L2, L3, L4)
      this.createTileLayers();
       
      // Extract placeholders from the object layer
      this.extractPlaceholders();
    } catch (error) {
      console.error('Error loading friends-ui map:', error);
      this.createFallbackPlaceholders();
    }
  }

  createTileLayers() {
    try {
      const layerNames = ['L1', 'L2', 'L3', 'L4'].reverse();
      const tileset = this.friendsUIMap.tilesets[0]; // Use first tileset
      
      // Create base UI layers only (no menu button layers)
      layerNames.forEach((layerName, index) => {
        try {
          const layer = this.friendsUIMap.createLayer(layerName, tileset, 0, 0);
          if (layer) {
            // Set appropriate depth for UI layers
            layer.setDepth(1400 + index); // Below overlay container but above game content
            layer.setScrollFactor(0); // Keep fixed to camera
            console.log(`Created UI tile layer: ${layerName}`);
          }
        } catch (layerError) {
          console.warn(`Could not create layer ${layerName}:`, layerError.message);
        }
      });
    } catch (error) {
      console.warn('Error creating tile layers:', error.message);
    }
  }

  extractPlaceholders() {
    try {
      // Get object layers from the tilemap
      const objectLayers = this.friendsUIMap.objects;
      
      if (objectLayers && objectLayers.length > 0) {
        console.log(`Found ${objectLayers.length} object layer(s)`);
        
        objectLayers.forEach((layer, layerIndex) => {
          console.log(`Processing object layer ${layerIndex}: ${layer.name || 'unnamed'}`);
          
          if (layer.objects && layer.objects.length > 0) {
            console.log(`Found ${layer.objects.length} objects in layer`);
            
            layer.objects.forEach((obj, objIndex) => {
              if (obj.name) {
                // Store placeholder information
                this.placeholders[obj.name] = {
                  x: obj.x,
                  y: obj.y,
                  width: obj.width,
                  height: obj.height,
                  type: obj.type || 'placeholder'
                };
                console.log(`Added placeholder: ${obj.name} at (${obj.x}, ${obj.y}) size ${obj.width}x${obj.height}`);
              } else {
                console.warn(`Object ${objIndex} in layer ${layerIndex} has no name`);
              }
            });
          } else {
            console.warn(`Object layer ${layerIndex} has no objects`);
          }
        });
        
        console.log('All extracted UI placeholders:', this.placeholders);
        
        // Check if we got any placeholders
        if (Object.keys(this.placeholders).length === 0) {
          console.warn('No named placeholders found, using fallback');
        }
      } else {
        console.warn('No object layers found in friends-ui map');
      }
    } catch (error) {
      console.error('Error extracting placeholders:', error);
    }
  }


  createOverlayBackground() {
    // Create overlay container at fixed position
    this.overlayContainer = this.scene.add.container(0, 0);
    
    // Set depth to render above everything else
    this.overlayContainer.setDepth(1500);
    this.overlayContainer.setScrollFactor(0); // Keep fixed to camera
    
    // Add background if there's a background placeholder
    if (this.placeholders['overlay-background']) {
      const bg = this.placeholders['overlay-background'];
      const background = this.scene.add.rectangle(
        bg.x + bg.width / 2, 
        bg.y + bg.height / 2, 
        bg.width, 
        bg.height, 
        0x2c3e50, 
        0.9
      );
      background.setStrokeStyle(2, 0x34495e);
      this.overlayContainer.add(background);
    }
  }

  setupFriendPlaceholders() {
    // Friend names mapping
    const friendNames = ['mo', 'julia', 'sam', 'liam', 'louisa'];
    
    friendNames.forEach(friendName => {
      const placeholder = this.placeholders[`placeholder-${friendName}`];
      
      if (placeholder) {
        // Calculate center position
        const centerX = placeholder.x + placeholder.width / 2;
        const centerY = placeholder.y + placeholder.height / 2;
        
        // Create hypno (empty) state image - always visible as background
        const hypnoImage = this.scene.add.image(centerX, centerY, `placeholder-${friendName}-hypno`);
        hypnoImage.setDisplaySize(placeholder.width, placeholder.height);
        this.overlayContainer.add(hypnoImage);
        
        // Create happy (rescued) state image - initially hidden
        const happyImage = this.scene.add.image(centerX, centerY, `placeholder-${friendName}-happy`);
        happyImage.setDisplaySize(placeholder.width, placeholder.height);
        happyImage.setVisible(false);
        this.overlayContainer.add(happyImage);
        
        // Store references for this friend slot
        this.friendSlots.push({
          name: friendName,
          hypnoImage: hypnoImage,
          happyImage: happyImage,
          placeholder: placeholder,
          rescued: false
        });
        
        console.log(`Created friend slot for ${friendName} at (${centerX}, ${centerY})`);
      } else {
        console.warn(`No placeholder found for friend: ${friendName}`);
      }
    });
  }

  createMenuButton() {
    // Use fallback position if no placeholder is found
    const menuPlaceholder = { x: 320, y: 0, width: 48, height: 48 };
    
    // Calculate center position
    const centerX = menuPlaceholder.x + menuPlaceholder.width / 2;
    const centerY = menuPlaceholder.y + menuPlaceholder.height / 2;
    
    // Create the sprite-based menu button - don't add to container to avoid interaction issues
    this.menuButton = this.scene.add.sprite(centerX, centerY, 'homebutton', 0); // Frame 0 = neutral
    this.menuButton.setInteractive({ useHandCursor: true });
    
    // Set proper depth to be above UI elements
    this.menuButton.setDepth(1520);
    this.menuButton.setScrollFactor(0); // Keep fixed to camera
    
    // Add hover effects
    this.menuButton.on('pointerover', () => {
      this.menuButton.setFrame(1); // Frame 1 = hover
      console.log('Menu button hover');
    });
    
    this.menuButton.on('pointerout', () => {
      this.menuButton.setFrame(0); // Frame 0 = neutral
      console.log('Menu button out');
    });
    
    this.menuButton.on('pointerdown', () => {
      this.menuButton.setFrame(2); // Frame 2 = down
      console.log('Menu button down');
    });
    
    this.menuButton.on('pointerup', () => {
      this.menuButton.setFrame(1); // Back to hover state
      this.onMenuButtonClick();
    });
    
    console.log(`Created sprite-based menu button at (${centerX}, ${centerY}) with depth 1520`);
  }
  
  createNavigationButtons() {
    // Load the controls tilemap
    let controlsMap;
    let controlsPlaceholders = {};
    
    try {
      controlsMap = this.scene.make.tilemap({ key: 'controls' });
      
      // Add tileset (assuming it uses the same 'ui' tileset)
      const controlsTileset = controlsMap.addTilesetImage('ui', 'ui');
      
      // Create tile layers from the controls tilemap at screen coordinates
      const layerNames = controlsMap.layers.map(layer => layer.name);
      layerNames.forEach((layerName, index) => {
        try {
          // Apply offset for symbols layer
          const offsetX = layerName === 'symbols' ? -8 : 0;
          const offsetY = layerName === 'symbols' ? 8 : 0;
          
          const layer = controlsMap.createLayer(layerName, controlsTileset, offsetX, offsetY);
          if (layer) {
            layer.setDepth(1400 + index); // Below navigation buttons but above other UI
            layer.setScrollFactor(0); // Keep fixed to camera
            console.log(`Created full-screen controls tile layer: ${layerName} at offset (${offsetX}, ${offsetY})`);
          }
        } catch (layerError) {
          console.warn(`Could not create controls layer ${layerName}:`, layerError.message);
        }
      });
      
      // Extract placeholders from controls tilemap (now at full-screen positions)
      const controlsObjectLayers = controlsMap.objects;
      if (controlsObjectLayers && controlsObjectLayers.length > 0) {
        controlsObjectLayers.forEach(layer => {
          if (layer.objects && layer.objects.length > 0) {
            layer.objects.forEach(obj => {
              if (obj.name) {
                controlsPlaceholders[obj.name] = {
                  x: obj.x,
                  y: obj.y,
                  width: obj.width,
                  height: obj.height
                };
                console.log(`Added controls placeholder: ${obj.name} at (${obj.x}, ${obj.y})`);
              }
            });
          }
        });
      }
    } catch (error) {
      console.warn('Could not load controls tilemap, using fallback positioning:', error.message);
      // Fallback to old positioning if tilemap fails
      this.createFallbackNavigationButtons();
      return;
    }
    
    // Navigation button configurations mapped to placeholder names
    const navButtons = [
      // Main directions
      { name: 'up', placeholder: 'up', x: 0, y: -1, color: 0x666666 },
      { name: 'down', placeholder: 'down', x: 0, y: 1, color: 0x666666 },
      { name: 'left', placeholder: 'left', x: -1, y: 0, color: 0x666666 },
      { name: 'right', placeholder: 'right', x: 1, y: 0, color: 0x666666 },
      
      // Diagonal directions
      { name: 'up-left', placeholder: 'upleft', x: -1, y: -1, color: 0x808080 },
      { name: 'up-right', placeholder: 'upright', x: 1, y: -1, color: 0x808080 },
      { name: 'down-left', placeholder: 'downleft', x: -1, y: 1, color: 0x808080 },
      { name: 'down-right', placeholder: 'downright', x: 1, y: 1, color: 0x808080 }
    ];
    
    this.navigationButtons = [];
    
    // Since the tilemap is now full-screen, placeholders are at correct positions
    navButtons.forEach(config => {
      const placeholder = controlsPlaceholders[config.placeholder];
      
      if (!placeholder) {
        console.warn(`No placeholder found for navigation button: ${config.placeholder}`);
        return;
      }
      
      // Use placeholder position directly (no translation needed)
      const buttonX = placeholder.x + placeholder.width / 2;
      const buttonY = placeholder.y + placeholder.height / 2;
      const buttonSize = Math.min(placeholder.width, placeholder.height);
      
      // Create button background
      const button = this.scene.add.rectangle(
        buttonX, 
        buttonY, 
        buttonSize, 
        buttonSize, 
        config.color, 
        0.25
      );
      button.setInteractive({ useHandCursor: true });
      button.setDepth(1510);
      button.setScrollFactor(0);
      
      // Store direction for movement
      button.direction = { x: config.x, y: config.y };
      button.isPressed = false;

      // Add hover effects
      button.on('pointerover', () => {
        if (!button.isPressed) {
          button.setFillStyle(config.color, 0.5);
          button.setScale(1.05);
        }
      });
      
      button.on('pointerout', () => {
        if (!button.isPressed) {
          button.setFillStyle(config.color, 0.25);
          button.setScale(1.0);
        }
      });
      
      // Handle continuous movement while button is held
      button.on('pointerdown', () => {
        button.isPressed = true;
        button.setFillStyle(config.color, 0.5);
        button.setScale(0.95);
        this.startContinuousMovement(config.x, config.y);
      });
      
      button.on('pointerup', () => {
        button.isPressed = false;
        button.setScale(1.0);
        button.setFillStyle(config.color, 0.25);
        this.stopContinuousMovement();
      });
      
      this.navigationButtons.push({ button, config });
      console.log(`Created navigation button '${config.name}' at (${buttonX}, ${buttonY}) from placeholder`);
    });
    
    console.log(`Created ${this.navigationButtons.length} navigation buttons from controls tilemap`);
  }
  
  createFallbackNavigationButtons() {
    // Fallback method using the original hardcoded positioning
    console.log('Using fallback navigation button positioning');
    
    // Position buttons in bottom-right corner
    const buttonSize = 50;
    const buttonSpacing = 60;
    const cornerPadding = 20;
    
    // Calculate base position (bottom-right corner)
    const baseX = this.scene.cameras.main.width - cornerPadding - buttonSpacing * 1.5;
    const baseY = this.scene.cameras.main.height - cornerPadding - buttonSpacing * 1.5;
    
    // Navigation button configurations
    const navButtons = [
      // Main directions
      { name: 'up', x: 0, y: -1, color: 0x666666 },
      { name: 'down', x: 0, y: 1, color: 0x666666 },
      { name: 'left', x: -1, y: 0, color: 0x666666 },
      { name: 'right', x: 1, y: 0, color: 0x666666 },
      
      // Diagonal directions
      { name: 'up-left', x: -1, y: -1, color: 0x808080 },
      { name: 'up-right', x: 1, y: -1, color: 0x808080 },
      { name: 'down-left', x: -1, y: 1, color: 0x808080 },
      { name: 'down-right', x: 1, y: 1, color: 0x808080 }
    ];
    
    this.navigationButtons = [];
    
    navButtons.forEach(config => {
      // Calculate button position relative to base
      const buttonX = baseX + (config.x * buttonSpacing);
      const buttonY = baseY + (config.y * buttonSpacing);
      
      // Create button background
      const button = this.scene.add.rectangle(
        buttonX, 
        buttonY, 
        buttonSize, 
        buttonSize, 
        config.color, 
        0.25
      );
      button.setStrokeStyle(2, 0xffffff, 0.5);
      button.setInteractive({ useHandCursor: true });
      button.setDepth(1510);
      button.setScrollFactor(0);

      // Store direction for movement
      button.direction = { x: config.x, y: config.y };
      button.isPressed = false;
      
      // Add hover effects
      button.on('pointerover', () => {
        if (!button.isPressed) {
          button.setFillStyle(config.color, 0.5);
          button.setScale(1.05);
        }
      });
      
      button.on('pointerout', () => {
        if (!button.isPressed) {
          button.setFillStyle(config.color, 0.25);
          button.setScale(1.0);
        }
      });
      
      // Handle continuous movement while button is held
      button.on('pointerdown', () => {
        button.isPressed = true;
        button.setFillStyle(config.color, 0.5);
        button.setScale(0.95);
        this.startContinuousMovement(config.x, config.y);
      });
      
      button.on('pointerup', () => {
        button.isPressed = false;
        button.setScale(1.0);
        button.setFillStyle(config.color, 0.25);
        this.stopContinuousMovement();
      });
      
      this.navigationButtons.push({ button, config });
    });
    
    console.log(`Created ${this.navigationButtons.length} fallback navigation buttons`);
  }

  handleNavigation(deltaX, deltaY) {
    // Check if the scene has a player object
    if (this.scene.player) {
      // Use the player's physics body for movement
      const speed = this.scene.player.speed || 150;
      
      // Set velocity for smooth movement
      this.scene.player.body.setVelocity(deltaX * speed, deltaY * speed);
      
      // Normalize diagonal movement to prevent faster diagonal speed
      this.scene.player.body.velocity.normalize().scale(speed);
      
      // Update player animation based on movement direction
      if (deltaX !== 0 || deltaY !== 0) {
        // Determine animation direction (prioritize horizontal movement for diagonals)
        let direction = 'south'; // default
        
        if (deltaX < 0) direction = 'west';
        else if (deltaX > 0) direction = 'east';
        else if (deltaY < 0) direction = 'north';
        else if (deltaY > 0) direction = 'south';
        
        // Play walking animation using the player's existing animation keys
        if (this.scene.player.anims) {
          const animKey = `walk-${direction}`;
          this.scene.player.play(animKey, true);
        }
      }
      
      console.log(`Navigation: applied velocity (${deltaX * speed}, ${deltaY * speed}) to player`);
    } else {
      console.warn('Navigation: No player object found in scene');
    }
  }
  
  startContinuousMovement(deltaX, deltaY) {
    // Stop any existing movement timer
    this.stopContinuousMovement();
    
    // Store current movement direction
    this.currentMovement = { x: deltaX, y: deltaY };
    
    // Apply initial movement
    this.handleNavigation(deltaX, deltaY);
    
    // Set up continuous movement timer
    this.movementTimer = this.scene.time.addEvent({
      delay: 16, // ~60 FPS for smooth movement
      callback: () => {
        this.handleNavigation(this.currentMovement.x, this.currentMovement.y);
      },
      loop: true
    });
    
    console.log(`Started continuous movement in direction (${deltaX}, ${deltaY})`);
  }
  
  stopContinuousMovement() {
    // Stop the movement timer
    if (this.movementTimer) {
      this.movementTimer.remove();
      this.movementTimer = null;
    }
    
    // Stop player movement and switch to idle
    if (this.scene.player && this.scene.player.body) {
      this.scene.player.body.setVelocity(0);
      
      // Determine idle direction based on last movement
      let direction = 'south'; // default
      if (this.currentMovement.x < 0) direction = 'west';
      else if (this.currentMovement.x > 0) direction = 'east';
      else if (this.currentMovement.y < 0) direction = 'north';
      else if (this.currentMovement.y > 0) direction = 'south';
      
      // Switch to idle animation
      const idleKey = `idle-${direction}`;
      if (this.scene.player.anims && this.scene.player.anims.exists(idleKey)) {
        this.scene.player.play(idleKey);
      }
    }
    
    // Reset movement direction
    this.currentMovement = { x: 0, y: 0 };
    
    console.log('Stopped continuous movement');
  }
  
  createFallbackMenuButton() {
    const menuPlaceholder = this.placeholders['menu-button'] || { x: 720, y: 20, width: 40, height: 32 };
    
    // Calculate center position
    const centerX = menuPlaceholder.x + menuPlaceholder.width / 2;
    const centerY = menuPlaceholder.y + menuPlaceholder.height / 2;
    
    // Button background
    this.menuButton = this.scene.add.rectangle(
      centerX, 
      centerY, 
      menuPlaceholder.width, 
      menuPlaceholder.height, 
      0xe74c3c
    );
    this.menuButton.setInteractive({ useHandCursor: true });
    this.overlayContainer.add(this.menuButton);
    
    // Button text (menu icon)
    const buttonText = this.scene.add.text(centerX, centerY, '≡', getTextStyle({
      fontSize: '18px',
      fill: '#fff'
    }));
    buttonText.setOrigin(0.5);
    this.overlayContainer.add(buttonText);
    
    // Add hover effects
    this.menuButton.on('pointerover', () => {
      this.menuButton.setFillStyle(0xc0392b);
    });
    
    this.menuButton.on('pointerout', () => {
      this.menuButton.setFillStyle(0xe74c3c);
    });
    
    // Menu button click handler
    this.menuButton.on('pointerdown', () => {
      this.onMenuButtonClick();
    });
    
    console.log(`Created fallback menu button at (${centerX}, ${centerY})`);
  }

  onMenuButtonClick() {
    console.log('Menu button clicked - going to StartScene');
    this.scene.scene.start('StartScene');
  }

  // Call this when a friend is successfully rescued
  rescueFriend(friendName) {
    const normalizedName = friendName.toLowerCase();
    const friendSlot = this.friendSlots.find(slot => slot.name === normalizedName);
    
    if (friendSlot && !friendSlot.rescued) {
      console.log(`Rescuing friend: ${friendName}`);
      
      // Hide hypno state (empty/hypnotized)
      friendSlot.hypnoImage.setVisible(false);
      
      // Show happy state (rescued)
      friendSlot.happyImage.setVisible(true);
      
      // Mark as rescued
      friendSlot.rescued = true;
      
      // Save progress
      this.saveRescuedFriends();
      
      // Add celebration animation
      this.scene.tweens.add({
        targets: friendSlot.happyImage,
        scaleX: 1.2,
        scaleY: 1.2,
        duration: 200,
        yoyo: true,
        ease: 'Back.easeOut'
      });
    }
  }

  updateFriendDisplay() {
    // Update the display based on saved progress
    this.friendSlots.forEach(slot => {
      if (this.rescuedFriends.includes(slot.name)) {
        // Show rescued state
        slot.hypnoImage.setVisible(false);
        slot.happyImage.setVisible(true);
        slot.rescued = true;
      } else {
        // Show hypnotized state  
        slot.hypnoImage.setVisible(true);
        slot.happyImage.setVisible(false);
        slot.rescued = false;
      }
    });
  }

  loadRescuedFriends() {
    // Load rescued friends from localStorage
    try {
      const saved = localStorage.getItem('hypnobreaker_rescued_friends');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading rescued friends:', error);
      return [];
    }
  }

  saveRescuedFriends() {
    // Save rescued friends to localStorage
    try {
      const rescuedNames = this.friendSlots
        .filter(slot => slot.rescued)
        .map(slot => slot.name);
      localStorage.setItem('hypnobreaker_rescued_friends', JSON.stringify(rescuedNames));
    } catch (error) {
      console.error('Error saving rescued friends:', error);
    }
  }

  // Clean up when scene is destroyed
  destroy() {
    // Stop any active movement
    this.stopContinuousMovement();
    
    if (this.overlayContainer) {
      this.overlayContainer.destroy();
    }
    
    // Clean up navigation buttons
    if (this.navigationButtons) {
      this.navigationButtons.forEach(navButton => {
        if (navButton.button) navButton.button.destroy();
        if (navButton.symbol) navButton.symbol.destroy();
      });
      this.navigationButtons = null;
    }
  }
}